import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import {
  Button, required, useTranslate, ValidationError,
} from 'react-admin';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { CertificationDocument, DocumentAssignee } from '../../../utils/certificationDocuments';
import type { CertificationsByAssetId, CertificationUserDetails } from '../../../utils/certificationUserForm';
import { TimeGranularityLabel } from '../../../utils/timeGranularityUtils';
import { CertificationDocumentPreviewButton } from './DocumentaryProof';
import { ExpiryDateDisplay, TABLE_EXPIRY_GRID_TEMPLATE } from './ExpiryDateDisplay';
import { CertificationDocumentInput } from './inputs/CertificationDocumentInput';
import { GrayDateInputWithUpperLabel } from './inputs/GrayDateInputWithUpperLabel';
import { notLaterThan, notLaterThanToday } from '../../../utils/dateValidators';

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/*': ['.jpg', '.jpeg', '.png'],
} as const;

const datesDifferFromFirst = (recipients: DocumentAssignee[], certificationsByAssetId: CertificationsByAssetId | undefined, includeFirstIssuedOn: boolean) => {

  if (!certificationsByAssetId || recipients.length < 2) return false;

  const first = certificationsByAssetId[recipients[0].id];
  if (!first) return false;

  return recipients.slice(1).some(({ id }) => {

    const next = certificationsByAssetId[id];

    if (!next) return true;

    return (
      next.lastReceivedOn !== first.lastReceivedOn
      || next.expiryDate !== first.expiryDate
      || (includeFirstIssuedOn && next.firstIssuedOn !== first.firstIssuedOn)
    );

  });

};

const ApplyDatesToAllButton = (props: {
  recipients: DocumentAssignee[];
  includeFirstIssuedOn: boolean;
}) => {

  const { getValues, setValue, trigger } = useFormContext();

  const apply = async () => {

    const firstId = props.recipients[0]?.id;
    if (!firstId) return;

    const base = `certificationsByAssetId.${firstId}`;
    const fields = [
      ...(props.includeFirstIssuedOn ? [`${base}.firstIssuedOn`] : []),
      `${base}.lastReceivedOn`,
      `${base}.expiryDate`,
    ];

    const valid = await trigger(fields, { shouldFocus: true });
    if (!valid) return;

    const first = getValues(base) as CertificationUserDetails;

    props.recipients.slice(1).forEach(({ id }) => {

      const target = `certificationsByAssetId.${id}`;
      if (props.includeFirstIssuedOn) {

        setValue(`${target}.firstIssuedOn`, first.firstIssuedOn, {
          shouldDirty: false,
          shouldValidate: true,
        });

      }
      setValue(`${target}.lastReceivedOn`, first.lastReceivedOn, {
        shouldDirty: false,
        shouldValidate: true,
      });
      setValue(`${target}.expiryDate`, first.expiryDate, {
        shouldDirty: true,
        shouldValidate: true,
      });

    });

  };

  return (
    <Button
      type="button"
      onClick={apply}
      label="resources.assets.certifications.text.apply_dates_to_all"
      size="small"
      sx={{ whiteSpace: 'nowrap' }}
    />
  );

};

const NoPreviewPlaceholder = () => {

  const translate = useTranslate();

  return (
    <Box
      sx={{
        width: 48,
        height: 64,
        boxSizing: 'border-box',
        bgcolor: 'grey.400',
        color: 'common.white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 0.5,
        borderRadius: 0.5,
      }}
    >
      <Typography variant="caption" sx={{ fontSize: 9, lineHeight: 1.1 }}>
        {translate('resources.assets.certifications.text.no_preview')}
      </Typography>
    </Box>
  );

};

const CertificationUserRow = (props: {
  recipient: DocumentAssignee;
  timeGranularity: ExpiryTimeGranularity;
  includeFirstIssuedOn: boolean;
  requiresDocument: boolean;
  isFirstRow: boolean;
  recipients: DocumentAssignee[];
  showApplyToAll: boolean;
}) => {

  const translate = useTranslate();
  const { setValue, getFieldState } = useFormContext();
  const base = `certificationsByAssetId.${props.recipient.id}`;
  const firstIssuedSource = `${base}.firstIssuedOn`;
  const lastReceivedSource = `${base}.lastReceivedOn`;
  const expiryDateSource = `${base}.expiryDate`;
  const documentSource = `${base}.document`;

  const formState = useFormState({
    name: [
      firstIssuedSource,
      lastReceivedSource,
      expiryDateSource,
    ],
  });

  const rowError = [
    getFieldState(firstIssuedSource, formState).error,
    getFieldState(lastReceivedSource, formState).error,
    getFieldState(expiryDateSource, formState).error,
  ].find(Boolean);

  const document = useWatch({ name: documentSource }) as CertificationDocument | undefined;
  const hasDocument = Boolean(document?.rawFile || document?.src);

  const removeDocument = () => {

    setValue(documentSource, undefined, {
      shouldDirty: true,
      shouldValidate: true,
    });

  };

  if (props.requiresDocument && !hasDocument) {

    return (
      <TableRow sx={{ height: 80 }}>
        <TableCell sx={{
          verticalAlign: 'middle',
          width: 140,
        }}>
          {props.recipient.label}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'middle', width: 104 }}>
          <NoPreviewPlaceholder />
        </TableCell>
        <TableCell colSpan={props.includeFirstIssuedOn ? 3 : 2}>
          <CertificationDocumentInput
            source={documentSource}
            label={false}
            validate={required()}
            labelSingle={translate(
              'resources.assets.certifications.text.drop_document_to_upload',
            )}
            accept={ACCEPT}
            sx={{ mb: 0 }}
          />
        </TableCell>
      </TableRow>
    );

  }

  return (
    <>
      <TableRow sx={{
        height: 80,
        ...(rowError?.message && {
          '& > td': {
            borderBottom: 'none',
          },
        }),
      }}>
        <TableCell sx={{ verticalAlign: 'middle', width: 140 }}>
          {props.recipient.label}
        </TableCell>

        {props.requiresDocument && (
          <TableCell sx={{
            verticalAlign: 'middle',
            width: 104,
          }}>
            <Box
              sx={{
                position: 'relative',
                width: 48,
                height: 64,
              }}
            >
              <CertificationDocumentPreviewButton source={documentSource} />
              <Tooltip title={translate('ra.input.image.remove_current')}>
                <IconButton
                  size="small"
                  onClick={removeDocument}
                  aria-label={translate('ra.input.image.remove_current')}
                  sx={{
                    position: 'absolute',
                    top: -7,
                    right: -7,
                    width: 22,
                    height: 22,
                    color: 'error.main',
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    zIndex: 1,
                    '&:hover': {
                      bgcolor: 'error.50',
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        )}

        {props.includeFirstIssuedOn && (
          <TableCell sx={{ verticalAlign: 'middle', width: 166, px: 1 }}>
            <GrayDateInputWithUpperLabel
              source={firstIssuedSource}
              label={false}
              validate={[
                required(),
                notLaterThanToday(
                  'resources.assets.certifications.validation.first_issued_in_future',
                ),
                notLaterThan(
                  lastReceivedSource,
                  'resources.assets.certifications.validation.first_issued_later_than_last_received',
                ),
              ]}
              helperText={false}
              size="small"
              sx={{
                '& .MuiFormHelperText-root': {
                  display: 'none',
                },
              }}
            />
          </TableCell>
        )}

        <TableCell sx={{ verticalAlign: 'middle' }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: 'fit-content',
          }}>
            <ExpiryDateDisplay
              startDateSource={lastReceivedSource}
              expiryDateSource={`${base}.expiryDate`}
              timeGranularity={props.timeGranularity}
              minDateSource={props.includeFirstIssuedOn ? firstIssuedSource : undefined}
              usage="table"
              hideFieldErrors
            />
            {props.isFirstRow && props.showApplyToAll && (
              <ApplyDatesToAllButton
                recipients={props.recipients}
                includeFirstIssuedOn={props.includeFirstIssuedOn}
              />
            )}
          </Box>
        </TableCell>
      </TableRow>

      {rowError?.message && (
        <TableRow>
          <TableCell
            colSpan={
              3
              + Number(props.requiresDocument)
              + Number(props.includeFirstIssuedOn)
            }
            sx={{
              color: 'error.main',
              fontSize: 12,
              pt: 0,
              pb: 1,
              borderTop: 0,
            }}
          >
            <ValidationError error={rowError.message} />
          </TableCell>
        </TableRow>
      )}
    </>
  );

};

export const CertificationUserTable = (props: {
  recipients: DocumentAssignee[];
  timeGranularity: ExpiryTimeGranularity;
  includeFirstIssuedOn: boolean;
  requiresDocument: boolean;
}) => {

  const translate = useTranslate();

  const certificationsByAssetId = useWatch({
    name: 'certificationsByAssetId',
  }) as CertificationsByAssetId | undefined;

  const hasAnyDateRow = !props.requiresDocument
    || props.recipients.some((recipient) => {

      const document = certificationsByAssetId?.[recipient.id]?.document;
      return Boolean(document?.rawFile || document?.src);

    });

  return (
    <TableContainer>
      <Table
        size="small"
        sx={{
          width: '100%',
          minWidth: props.includeFirstIssuedOn ? 900 : 760,
          tableLayout: 'fixed',
        }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 140 }}>
              {translate('general.fields.name')}
            </TableCell>
            {props.requiresDocument && (
              <TableCell
                sx={{
                  width: 104,
                }}
              >
                {translate('resources.assets.certifications.fields.documentary_proof')}
              </TableCell>
            )}
            {props.includeFirstIssuedOn && (
              <TableCell sx={{ width: 166, px: 1, textAlign: 'center' }}>
                {hasAnyDateRow
                  ? translate(
                    'resources.assets.certifications.fields.first_issued_on',
                  )
                  : null}
              </TableCell>
            )}
            <TableCell>
              {hasAnyDateRow && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: TABLE_EXPIRY_GRID_TEMPLATE,
                    columnGap: 2,
                    width: 'fit-content',
                  }}
                >
                  <Typography variant="body2" fontWeight={500} textAlign="center">
                    {translate(
                      'resources.assets.certifications.fields.last_received_on',
                    )}
                  </Typography>

                  <Chip
                    label={TimeGranularityLabel(
                      props.timeGranularity,
                      translate,
                    )}
                    sx={{ visibility: 'hidden' }}
                  />

                  <Typography
                    variant="body2"
                    fontWeight={500}
                    textAlign="center"
                  >
                    {translate(
                      'resources.assets.certifications.fields.expiry_date',
                    )}
                  </Typography>
                </Box>
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.recipients.map((recipient, index) => (
            <CertificationUserRow
              key={recipient.id}
              recipient={recipient}
              timeGranularity={props.timeGranularity}
              includeFirstIssuedOn={props.includeFirstIssuedOn}
              requiresDocument={props.requiresDocument}
              isFirstRow={index === 0}
              recipients={props.recipients}
              showApplyToAll={datesDifferFromFirst(props.recipients, certificationsByAssetId, props.includeFirstIssuedOn)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

};
